import { goalbookUrls } from '@/util/links'

export interface AuditResult {
  section: string
  audit_title: string
  score: number
  max_score: number
  score_rationale: string
  suggestions: string
}

export interface CriteriaItem {
  label: string
  description: string
  subjects: string[]
  grades?: string[]
  order: number
  released?: boolean
  beta_only?: boolean
}

export interface ReviewCriteriaProfile {
  content_profile: string
  criteria_set_name: string
  criteria_by_type: Record<string, CriteriaItem[]>
}

export async function getContentReviewProfiles(): Promise<ReviewCriteriaProfile[]> {
  const response = await fetch(`${goalbookUrls.pathwaysApi}/content_review/profiles`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  })
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }
  return response.json()
}

const reviewStreamUrl = `${goalbookUrls.pathwaysApi}/content_review/review_stream`

function stripMarkdownFences(content: string): string {
  let cleaned = content.trim()
  if (cleaned.startsWith('```json')) cleaned = cleaned.slice(7)
  else if (cleaned.startsWith('```')) cleaned = cleaned.slice(3)
  if (cleaned.endsWith('```')) cleaned = cleaned.slice(0, -3)
  return cleaned.trim()
}

interface ReviewStreamOptions {
  betaMode?: boolean
  signal?: AbortSignal
  onProgress?: (partialContent: string) => void
}

export interface ReviewImpactStatementStreamArgs extends ReviewStreamOptions {
  impactStatementText: string
  contentProfile: string
  subjects: string[]
}

export interface ReviewPresentLevelsStreamArgs extends ReviewStreamOptions {
  presentLevelsText: string
  contentProfile: string
  subjects: string[]
}

export interface ReviewPresentLevelsWithImpactStreamArgs extends ReviewStreamOptions {
  presentLevelsText: string
  impactStatementText: string
  contentProfile: string
  subjects: string[]
}

export interface ReviewGoalStreamArgs extends ReviewStreamOptions {
  goalUuid: string
  goalText: string
  objectiveTexts: string[]
  contentProfile: string
}

export type AuditResponse = { audit_results: AuditResult[] }

interface ReviewStreamRequest extends ReviewStreamOptions {
  contentProfile: string
  subjects: string[]
  content: Record<string, unknown>
  reviewName: string
}

async function streamContentReview({
  contentProfile,
  subjects,
  content,
  reviewName,
  betaMode = false,
  signal,
  onProgress,
}: ReviewStreamRequest): Promise<AuditResponse> {
  const response = await fetch(reviewStreamUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      content_profile: contentProfile,
      grades: [],
      subjects,
      beta_mode: betaMode,
      content,
    }),
    credentials: 'include',
    signal,
  })

  if (!response.ok || !response.body) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let fullContent = ''

  try {
    let isStreamDone = false
    while (!isStreamDone) {
      const { done, value } = await reader.read()
      isStreamDone = done
      if (!done) {
        buffer += decoder.decode(value, { stream: true })
      } else {
        buffer += decoder.decode()
      }
      const lines = buffer.split('\n')
      buffer = done ? '' : (lines.pop() || '')

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue
        const dataStr = line.slice(6)
        if (!dataStr.trim()) continue

        let data: Record<string, unknown>
        try {
          data = JSON.parse(dataStr)
        } catch (parseError) {
          console.error('[STREAM] Error parsing SSE data:', parseError)
          continue
        }

        if (data.error) throw new Error(data.error as string)
        if (data.done) {
          return JSON.parse(stripMarkdownFences(fullContent))
        }
        if (data.content) {
          fullContent += data.content
          onProgress?.(fullContent)
        }
      }
    }

    if (fullContent) {
      return JSON.parse(stripMarkdownFences(fullContent))
    }
    throw new Error('Stream ended without completion signal')
  } catch (error) {
    if ((error as Error).name === 'AbortError') throw error
    console.error(`[STREAM] Fatal error in ${reviewName} review stream:`, error)
    throw error
  }
}

export function reviewImpactStatementStream({
  impactStatementText,
  ...request
}: ReviewImpactStatementStreamArgs): Promise<AuditResponse> {
  return streamContentReview({
    ...request,
    content: { impact: { impact_statement_text: impactStatementText } },
    reviewName: 'impact statement',
  })
}

export function reviewPresentLevelsStream({
  presentLevelsText,
  ...request
}: ReviewPresentLevelsStreamArgs): Promise<AuditResponse> {
  return streamContentReview({
    ...request,
    content: { present_levels: { present_levels_text: presentLevelsText } },
    reviewName: 'present levels',
  })
}

export function reviewPresentLevelsWithImpactStream({
  presentLevelsText,
  impactStatementText,
  ...request
}: ReviewPresentLevelsWithImpactStreamArgs): Promise<AuditResponse> {
  // Only include a content type when its text is non-empty — the backend returns 400
  // if a content key is present but the required text field is empty.
  const content: Record<string, unknown> = {}
  if (presentLevelsText.trim()) {
    content.present_levels = { present_levels_text: presentLevelsText }
  }
  if (impactStatementText.trim()) {
    content.impact = { impact_statement_text: impactStatementText }
  }

  return streamContentReview({ ...request, content, reviewName: 'present levels + impact' })
}

export function reviewGoalStream({
  goalUuid,
  goalText,
  objectiveTexts,
  ...request
}: ReviewGoalStreamArgs): Promise<AuditResponse> {
  return streamContentReview({
    ...request,
    subjects: [],
    content: {
      goals: { goal_uuid: goalUuid, goal_text: goalText, objective_texts: objectiveTexts },
    },
    reviewName: 'goal',
  })
}
